import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/modules/shared/components/ui/table'
import { cn } from '@/modules/shared/lib/utils'
import { numberFormatter, priceFormatter } from '@/modules/shared/utils/formatter'

import type { DemonstrativoMetaFamilia } from '../types/demonstrativo-meta-familia'

type DemonstrativoMetaFamiliaTableComponentProps = {
  data: DemonstrativoMetaFamilia[]
  isLoading: boolean
  metaPorQuantidade?: boolean
}

type MobileValueProps = {
  label: string
  value: number | string
  align?: 'left' | 'right'
  highlight?: boolean
  goalAchieved?: boolean
  metaPorQuantidade?: boolean
}

function MobileValue({
  label,
  value,
  align = 'left',
  highlight = false,
  goalAchieved = false,
  metaPorQuantidade = false,
}: MobileValueProps) {
  return (
    <div className={cn(align === 'right' && 'text-right')}>
      <p className="text-[10px] font-medium uppercase text-gray-500">{label}</p>
      <p
        className={cn(
          'mt-0.5 text-[13px] font-semibold leading-tight text-gray-900',
          highlight && 'text-primary',
          goalAchieved && 'text-green-600',
        )}
      >
        {typeof value === 'number'
          ? formatMetaValue(value, metaPorQuantidade)
          : value}
      </p>
    </div>
  )
}

type MobileSectionProps = {
  title: string
  children: ReactNode
}

function MobileSection({ title, children }: MobileSectionProps) {
  return (
    <div className="rounded-md bg-gray-50 p-2">
      <p className="mb-2 text-[10px] font-bold uppercase text-gray-500">
        {title}
      </p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2">{children}</div>
    </div>
  )
}

function formatMetaValue(value: number, metaPorQuantidade?: boolean) {
  return metaPorQuantidade
    ? numberFormatter.format(value)
    : priceFormatter.format(value)
}

export function DemonstrativoMetaFamiliaTableComponent({
  data,
  isLoading,
  metaPorQuantidade = false,
}: DemonstrativoMetaFamiliaTableComponentProps) {
  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (data.length <= 0) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-md border border-gray-200 bg-gray-50 px-3">
        <p className="text-center text-sm text-gray-500">
          Não há metas para esta semana
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="hidden h-full overflow-auto md:block">
        <Table>
          <TableHeader>
            <TableRow className="text-xs uppercase">
              <TableHead
                className="sticky top-0 z-20 min-w-40 bg-white font-bold shadow-xs"
                rowSpan={2}
              >
                Família
              </TableHead>
              <TableHead
                className="sticky top-0 z-20 border-l border-gray-200 bg-white text-center font-bold shadow-xs"
                colSpan={4}
              >
                Semana atual
              </TableHead>
              <TableHead
                className="sticky top-0 z-20 border-l border-gray-200 bg-white text-center font-bold shadow-xs"
                colSpan={3}
              >
                Semana anterior
              </TableHead>
              <TableHead
                className="sticky top-0 z-20 border-l border-gray-200 bg-white text-center font-bold shadow-xs"
                colSpan={3}
              >
                Acumulado
              </TableHead>
            </TableRow>
            <TableRow className="text-xs uppercase">
              <TableHead className="sticky top-10 z-20 min-w-28 border-l border-gray-200 bg-white text-right font-bold shadow-xs">
                Realizado
              </TableHead>
              <TableHead className="sticky top-10 z-20 min-w-28 bg-white text-right font-bold shadow-xs">
                Meta
              </TableHead>
              <TableHead className="sticky top-10 z-20 min-w-20 bg-white text-right font-bold shadow-xs">
                %
              </TableHead>
              <TableHead className="sticky top-10 z-20 min-w-28 bg-white text-right font-bold shadow-xs">
                Saldo
              </TableHead>
              <TableHead className="sticky top-10 z-20 min-w-28 border-l border-gray-200 bg-white text-right font-bold shadow-xs">
                Realizado
              </TableHead>
              <TableHead className="sticky top-10 z-20 min-w-28 bg-white text-right font-bold shadow-xs">
                Meta
              </TableHead>
              <TableHead className="sticky top-10 z-20 min-w-28 bg-white text-right font-bold shadow-xs">
                Saldo
              </TableHead>
              <TableHead className="sticky top-10 z-20 min-w-28 border-l border-gray-200 bg-white text-right font-bold shadow-xs">
                Realizado
              </TableHead>
              <TableHead className="sticky top-10 z-20 min-w-28 bg-white text-right font-bold shadow-xs">
                Meta
              </TableHead>
              <TableHead className="sticky top-10 z-20 min-w-28 bg-white text-right font-bold shadow-xs">
                Saldo
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.map((item) => (
              <TableRow
                className="text-xs sm:text-sm"
                key={`${item.fkFamilia}-${item.idCalendario}`}
              >
                <TableCell className="font-medium">{item.familia}</TableCell>
                <TableCell className="border-l border-gray-100 text-right">
                  {formatMetaValue(item.valorRealizado, metaPorQuantidade)}
                </TableCell>
                <TableCell className="text-right">
                  {formatMetaValue(item.valorMetaSemana, metaPorQuantidade)}
                </TableCell>
                <TableCell className="text-right">
                  {item.porcentagem.toFixed(2)}%
                </TableCell>
                <TableCell className="text-right">
                  {formatMetaValue(item.valorSaldo, metaPorQuantidade)}
                </TableCell>
                <TableCell className="border-l border-gray-100 text-right">
                  {formatMetaValue(
                    item.valorRealizadoSemAnt,
                    metaPorQuantidade,
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {formatMetaValue(item.valorMetaSemAnt, metaPorQuantidade)}
                </TableCell>
                <TableCell className="text-right">
                  {formatMetaValue(item.valorSaldoSemAnt, metaPorQuantidade)}
                </TableCell>
                <TableCell className="border-l border-gray-100 text-right">
                  {formatMetaValue(
                    item.valorRealizadoAcumulado,
                    metaPorQuantidade,
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {formatMetaValue(item.valorMetaAcumulada, metaPorQuantidade)}
                </TableCell>
                <TableCell className="text-right">
                  {formatMetaValue(item.valorSaldoAcumulado, metaPorQuantidade)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="h-full space-y-3 overflow-auto pr-1 md:hidden">
        {data.map((item) => (
          <div
            className="rounded-md border border-gray-200 bg-white p-3 shadow-xs"
            key={`${item.fkFamilia}-${item.idCalendario}`}
          >
            <div className="mb-3 flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
              <p className="text-sm font-bold uppercase leading-snug text-gray-800">
                {item.familia}
              </p>
              <p
                className={cn(
                  'shrink-0 rounded-md px-2 py-1 text-xs font-bold',
                  item.porcentagem >= 100
                    ? 'bg-green-50 text-green-600'
                    : 'bg-primary/10 text-primary',
                )}
              >
                {item.porcentagem.toFixed(2)}%
              </p>
            </div>

            <div className="space-y-2">
              <MobileSection title="Semana atual">
                <MobileValue
                  goalAchieved={item.porcentagem >= 100}
                  label="Realizado"
                  metaPorQuantidade={metaPorQuantidade}
                  value={item.valorRealizado}
                />
                <MobileValue
                  align="right"
                  label="Meta"
                  metaPorQuantidade={metaPorQuantidade}
                  value={item.valorMetaSemana}
                />
                <MobileValue
                  goalAchieved={item.porcentagem >= 100}
                  highlight
                  label="Saldo"
                  metaPorQuantidade={metaPorQuantidade}
                  value={item.valorSaldo}
                />
                <MobileValue
                  align="right"
                  goalAchieved={item.porcentagem >= 100}
                  highlight
                  label="%"
                  value={`${item.porcentagem.toFixed(2)}%`}
                />
              </MobileSection>

              <MobileSection title="Semana anterior">
                <MobileValue
                  goalAchieved={
                    item.valorMetaSemAnt > 0 &&
                    item.valorRealizadoSemAnt >= item.valorMetaSemAnt
                  }
                  label="Realizado"
                  metaPorQuantidade={metaPorQuantidade}
                  value={item.valorRealizadoSemAnt}
                />
                <MobileValue
                  align="right"
                  label="Meta"
                  metaPorQuantidade={metaPorQuantidade}
                  value={item.valorMetaSemAnt}
                />
                <MobileValue
                  goalAchieved={
                    item.valorMetaSemAnt > 0 &&
                    item.valorRealizadoSemAnt >= item.valorMetaSemAnt
                  }
                  label="Saldo"
                  metaPorQuantidade={metaPorQuantidade}
                  value={item.valorSaldoSemAnt}
                />
              </MobileSection>

              <MobileSection title="Acumulado">
                <MobileValue
                  goalAchieved={
                    item.valorMetaAcumulada > 0 &&
                    item.valorRealizadoAcumulado >= item.valorMetaAcumulada
                  }
                  label="Realizado"
                  metaPorQuantidade={metaPorQuantidade}
                  value={item.valorRealizadoAcumulado}
                />
                <MobileValue
                  align="right"
                  label="Meta"
                  metaPorQuantidade={metaPorQuantidade}
                  value={item.valorMetaAcumulada}
                />
                <MobileValue
                  goalAchieved={
                    item.valorMetaAcumulada > 0 &&
                    item.valorRealizadoAcumulado >= item.valorMetaAcumulada
                  }
                  highlight
                  label="Saldo"
                  metaPorQuantidade={metaPorQuantidade}
                  value={item.valorSaldoAcumulado}
                />
              </MobileSection>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
